import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { toast } from "./Toast";
import { media, entities as entitiesApi } from "../services/api";
import { useApp } from "../context/AppContext";
import {
  findDuplicateGroups,
  getIgnoredPairs,
  addIgnoredPair,
} from "../utils/duplicateDetection";
import {
  ProductIcon,
  MergeIcon,
  CheckIcon,
  ChevronRightIcon,
  AlertIcon,
} from "./Icons";

function DuplicateScannerModal({ isOpen, onClose }) {
  const { refreshProducts } = useApp();

  const [step, setStep] = useState("scanning"); // scanning | reviewing | complete
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState(null);
  const [merging, setMerging] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [stats, setStats] = useState({ reviewed: 0, merged: 0, ignored: 0 });

  // Scan for duplicates when modal opens
  useEffect(() => {
    if (isOpen) {
      scanForDuplicates();
    } else {
      // Reset state when closed
      setStep("scanning");
      setDuplicateGroups([]);
      setCurrentGroupIndex(0);
      setSelectedPrimaryId(null);
      setScanError(false);
      setStats({ reviewed: 0, merged: 0, ignored: 0 });
    }
  }, [isOpen]);

  const scanForDuplicates = async () => {
    setStep("scanning");
    setScanError(false);
    try {
      // Fetch all products
      const response = await entitiesApi.list({
        entity_type: "product",
        limit: 5000,
      });
      const products = response.data?.entities || response.data || [];

      const ignoredPairs = getIgnoredPairs();
      const groups = findDuplicateGroups(products, ignoredPairs);

      setDuplicateGroups(groups);
      setStep(groups.length > 0 ? "reviewing" : "complete");

      if (groups.length > 0) {
        setSelectedPrimaryId(groups[0].products[0].id);
      }
    } catch (err) {
      console.error("Failed to scan for duplicates:", err);
      setScanError(true);
      setStep("complete");
    }
  };

  const currentGroup = duplicateGroups[currentGroupIndex];

  const handleIgnore = () => {
    // Add all pairs in current group to ignored
    const products = currentGroup.products;
    for (let i = 0; i < products.length; i++) {
      for (let j = i + 1; j < products.length; j++) {
        addIgnoredPair(products[i].id, products[j].id);
      }
    }

    setStats((s) => ({ ...s, reviewed: s.reviewed + 1, ignored: s.ignored + 1 }));
    goToNextGroup();
  };

  const handleMerge = async () => {
    if (!selectedPrimaryId) return;

    setMerging(true);
    try {
      const otherIds = currentGroup.products
        .filter((p) => p.id !== selectedPrimaryId)
        .map((p) => p.id);

      await entitiesApi.merge(selectedPrimaryId, otherIds);

      setStats((s) => ({ ...s, reviewed: s.reviewed + 1, merged: s.merged + 1 }));

      // Remove merged products from remaining groups
      const remainingGroups = duplicateGroups.slice(currentGroupIndex + 1);
      const updatedGroups = remainingGroups
        .map((group) => ({
          ...group,
          products: group.products.filter((p) => !otherIds.includes(p.id)),
        }))
        .filter((g) => g.products.length > 1);

      if (updatedGroups.length === 0) {
        setStep("complete");
        refreshProducts();
      } else {
        setDuplicateGroups(updatedGroups);
        setCurrentGroupIndex(0);
        setSelectedPrimaryId(updatedGroups[0].products[0].id);
      }
    } catch (err) {
      console.error("Failed to merge:", err);
      toast.error("Failed to merge products");
    } finally {
      setMerging(false);
    }
  };

  const goToNextGroup = () => {
    if (currentGroupIndex < duplicateGroups.length - 1) {
      const nextIndex = currentGroupIndex + 1;
      setCurrentGroupIndex(nextIndex);
      setSelectedPrimaryId(duplicateGroups[nextIndex].products[0].id);
    } else {
      setStep("complete");
      refreshProducts();
    }
  };

  const handleClose = () => {
    if (merging) return;
    if (stats.merged > 0) {
      refreshProducts();
    }
    onClose();
  };

  // Render content based on step
  const renderContent = () => {
    switch (step) {
      case "scanning":
        return (
          <div style={{ textAlign: "center", padding: "var(--spacing-2xl)" }}>
            <div className="spinner" />
            <p style={{ marginTop: "var(--spacing-md)", color: "var(--text-secondary)" }}>
              Scanning for duplicates...
            </p>
          </div>
        );

      case "reviewing":
        return (
          <>
            <div style={{ marginBottom: "var(--spacing-md)" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "var(--spacing-sm)",
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  Group {currentGroupIndex + 1} of {duplicateGroups.length}
                </span>
                <span
                  className="badge"
                  style={{
                    background:
                      currentGroup.matchType === "sku"
                        ? "var(--success)"
                        : currentGroup.matchType === "exact"
                          ? "var(--warning)"
                          : "var(--info)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "0.75rem",
                  }}
                >
                  {currentGroup.matchType === "sku"
                    ? "Same SKU"
                    : currentGroup.matchType === "exact"
                      ? "Exact Match"
                      : "Similar Names"}
                </span>
              </div>
              <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                Select which item to keep. Others will be merged into it.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-sm)",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              {currentGroup.products.map((product) => (
                <div
                  key={product.id}
                  onClick={() => setSelectedPrimaryId(product.id)}
                  className="card"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-md)",
                    padding: "var(--spacing-md)",
                    cursor: "pointer",
                    border:
                      selectedPrimaryId === product.id
                        ? "2px solid var(--accent-primary)"
                        : "1px solid var(--border-color)",
                    background:
                      selectedPrimaryId === product.id
                        ? "var(--bg-tertiary)"
                        : "var(--bg-primary)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  {/* Radio indicator */}
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid var(--accent-primary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {selectedPrimaryId === product.id && (
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          background: "var(--accent-primary)",
                        }}
                      />
                    )}
                  </div>

                  {/* Product image */}
                  {product.image_path ? (
                    <img
                      src={media.getImageUrl(product.image_path)}
                      alt=""
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "var(--radius-sm)",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "var(--bg-secondary)",
                        borderRadius: "var(--radius-sm)",
                        color: "var(--text-muted)",
                        flexShrink: 0,
                      }}
                    >
                      <ProductIcon size={24} />
                    </div>
                  )}

                  {/* Product info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {product.name}
                    </div>
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                        display: "flex",
                        gap: "var(--spacing-md)",
                        flexWrap: "wrap",
                      }}
                    >
                      {product.sku && <span>SKU: {product.sku}</span>}
                      {product.quantity > 1 && <span>Qty: {product.quantity}</span>}
                      {product.list_sale_price != null && (
                        <span>${parseFloat(product.list_sale_price).toFixed(2)}</span>
                      )}
                    </div>
                  </div>

                  {selectedPrimaryId === product.id && (
                    <span
                      style={{
                        color: "var(--accent-primary)",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        flexShrink: 0,
                      }}
                    >
                      Keep
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        );

      case "complete":
        if (scanError) {
          return (
            <div style={{ textAlign: "center", padding: "var(--spacing-2xl)" }}>
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  background: "var(--danger, #ef4444)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto var(--spacing-lg)",
                }}
              >
                <AlertIcon size={32} />
              </div>
              <h3 style={{ marginBottom: "var(--spacing-sm)" }}>Scan Failed</h3>
              <p style={{ color: "var(--text-secondary)" }}>
                Couldn't load your products to check for duplicates. Check your
                connection and try again.
              </p>
            </div>
          );
        }
        return (
          <div style={{ textAlign: "center", padding: "var(--spacing-2xl)" }}>
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "var(--success)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto var(--spacing-lg)",
              }}
            >
              <CheckIcon size={32} color="white" />
            </div>
            <h3 style={{ marginBottom: "var(--spacing-sm)" }}>
              {duplicateGroups.length === 0 && stats.merged === 0
                ? "No Duplicates Found"
                : "Review Complete"}
            </h3>
            {stats.merged > 0 || stats.ignored > 0 ? (
              <p style={{ color: "var(--text-secondary)" }}>
                {stats.merged > 0 &&
                  `${stats.merged} group${stats.merged > 1 ? "s" : ""} merged. `}
                {stats.ignored > 0 &&
                  `${stats.ignored} group${stats.ignored > 1 ? "s" : ""} ignored.`}
              </p>
            ) : (
              <p style={{ color: "var(--text-secondary)" }}>
                Your product inventory has no duplicate items.
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (step === "scanning") return null;

    if (step === "complete") {
      return (
        <button onClick={handleClose} className="btn btn-primary">
          Done
        </button>
      );
    }

    return (
      <>
        <button onClick={handleIgnore} className="btn btn-secondary" disabled={merging}>
          Ignore
        </button>
        <button
          onClick={goToNextGroup}
          className="btn btn-secondary"
          disabled={merging || currentGroupIndex >= duplicateGroups.length - 1}
          style={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          Skip
          <ChevronRightIcon size={16} />
        </button>
        <button
          onClick={handleMerge}
          className="btn btn-primary"
          disabled={merging || !selectedPrimaryId}
          style={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          {merging ? (
            "Merging..."
          ) : (
            <>
              <MergeIcon size={16} />
              Merge ({currentGroup?.products.length || 0} items)
            </>
          )}
        </button>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Find Duplicates"
      size="large"
      footer={renderFooter()}
      closeOnOverlayClick={!merging}
    >
      {renderContent()}
    </Modal>
  );
}

export default DuplicateScannerModal;
